"use client";

import { useEffect, useRef, useState } from "react";
import { ImageOff, Loader2 } from "lucide-react";
import { getCachedImageBlob, cacheImage } from "../../lib/imageCache.js";

interface OfflineImageProps {
  /** Stable identifier for this image (the file's own id) — used as the
   * offline cache key since the live `src` URL (presigned S3, etc.) can
   * change or expire on every request. */
  fileId: string;
  src?: string;
  alt: string;
  className?: string;
}

/**
 * Renders a server-hosted image with an offline fallback.
 *
 * - Online: shows the live `src` immediately, and warms the offline cache
 *   for this file in the background so it stays viewable later.
 * - Offline (or the live URL fails/expires): falls back to the last cached
 *   copy of this exact file, if one was ever successfully loaded before.
 * - Never cached and unreachable: shows a clear "unavailable offline"
 *   placeholder instead of a broken image icon.
 */
export function OfflineImage({
  fileId,
  src,
  alt,
  className,
}: OfflineImageProps) {
  const [displaySrc, setDisplaySrc] = useState<string | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "unavailable">(
    "loading",
  );
  const objectUrlRef = useRef<string | null>(null);
  const [online, setOnline] = useState(() => navigator.onLine !== false);
  const generation = useRef(0);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine !== false);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadGeneration = ++generation.current;

    async function load() {
      setStatus("loading");
      const localSource = /^(blob:|data:)/.test(src || "");
      if (localSource) {
        setDisplaySrc(src!);
        setStatus("ready");
        cacheImage(fileId, src);
        return;
      }

      if (!src || !online) {
        const blob = await getCachedImageBlob(fileId, src);
        if (cancelled) return;
        if (blob) {
          if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
          const objectUrl = URL.createObjectURL(blob);
          objectUrlRef.current = objectUrl;
          setDisplaySrc(objectUrl);
          setStatus("ready");
        } else {
          setStatus("unavailable");
        }
        return;
      }

      // Online with a live URL: show it directly, then warm the offline
      // cache in the background for next time.
      setDisplaySrc(src);
      setStatus("ready");
      cacheImage(fileId, src);
    }

    load();

    return () => {
      cancelled = true;
      generation.current = loadGeneration + 1;
    };
  }, [fileId, src, online]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  const handleError = async () => {
    const currentGeneration = generation.current;
    if (displaySrc === objectUrlRef.current && objectUrlRef.current) {
      setStatus("unavailable");
      return;
    }
    // The live URL failed (expired presigned link, dropped mid-load,
    // etc.) — fall back to whatever's cached for this exact file.
    await cacheImage(fileId, src);
    const blob = await getCachedImageBlob(fileId, src);
    if (generation.current !== currentGeneration) return;
    if (blob) {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const objectUrl = URL.createObjectURL(blob);
      objectUrlRef.current = objectUrl;
      setDisplaySrc(objectUrl);
      setStatus("ready");
    } else {
      setStatus("unavailable");
    }
  };

  if (status === "loading") {
    return (
      <div
        className={`flex items-center justify-center bg-muted ${className || ""}`}
      >
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (status === "unavailable") {
    return (
      <div
        className={`flex flex-col items-center justify-center gap-1 bg-muted text-muted-foreground ${className || ""}`}
      >
        <ImageOff className="w-5 h-5" />
        <span className="text-[10px] text-center px-1">
          Not available offline
        </span>
      </div>
    );
  }

  return (
    <img
      loading="lazy"
      decoding="async"
      src={displaySrc || "/placeholder.svg"}
      alt={alt}
      className={className}
      onError={handleError}
    />
  );
}
